import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { BoldIcon, BracesIcon, ChevronDownIcon, ItalicIcon, ListIcon, ListOrderedIcon, UnderlineIcon } from './icons';
import type { IMergeFieldToken } from './mergeFieldSerialization';
import type { IMergeFieldAttrs } from './mergeFieldToken';
import { MergeField } from './mergeFieldToken';
import { CONTACT_MERGE_FIELDS } from './placeholders';
import { styles } from './styles';

/**
 * Stamped on every paragraph, mirroring legacy TinyMCE's forced_root_block_attrs
 * (DefaultRawRichTextContentBlockStyleAttribute in extViewmodels/sdk/Utils.ts): v2 and
 * legacy documents render identically everywhere, AND the UA's default <p> margin is
 * neutralized — without this the caret sits ~14px below the placeholder (no CSS reset
 * exists in the embed environment).
 */
const LEGACY_BLOCK_STYLE = 'margin:0;padding:0;font-size:14px;font-family:"Sora",sans-serif;';

/**
 * The plugin embed host ships a global reset (`ul,li { list-style:none; padding:0; margin:0 }`) and
 * v2 cannot ship a CSS file to undo it, so restore list markers + indent inline on the list itself.
 * `list-style:inherit` on the item is what actually re-shows the marker — the reset's explicit
 * `list-style:none` on `li` beats the inherited type otherwise.
 */
const BULLET_LIST_STYLE = 'list-style-type:disc;padding-left:24px;margin:4px 0;';
const ORDERED_LIST_STYLE = 'list-style-type:decimal;padding-left:24px;margin:4px 0;';
const LIST_ITEM_STYLE = 'list-style:inherit;';

export interface IRichTextChangeMeta {
	isEmpty: boolean;
}

interface IProps {
	initialContent: string;
	onChange: (html: string, meta: IRichTextChangeMeta) => void;
	/** Fired when the editing surface loses focus (e.g. to commit/persist on blur). */
	onBlur?: () => void;
	/** Shown over the editing surface while the document is empty. */
	placeholder?: string;
	/** Toolbar formatting controls (bold/italic/underline/lists). Default true. */
	showFormatting?: boolean;
	/** Show the Lists dropdown within the formatting controls. Default true. */
	showLists?: boolean;
	/** Show the built-in contact merge-field dropdown. Default true. Ignored when `renderMergeField` is supplied. */
	showMergeField?: boolean;
	/**
	 * Replace the built-in contact merge-field dropdown with a custom control (e.g. a design-system
	 * dropdown). Receives an `insertMergeField` callback to insert the chosen field at the caret.
	 */
	renderMergeField?: (insertMergeField: (field: IMergeFieldAttrs) => void) => ReactNode;
	/** Inline style stamped on each paragraph. Default: the legacy Sora block style. */
	paragraphStyle?: string;
	/** Style overrides merged onto the editing surface (e.g. a themed border for the host app). */
	surfaceStyle?: CSSProperties;
	/** Tokens converted to pills as the user types a known `{{...}}` (see MergeField input rules). */
	mergeFieldTokens?: readonly IMergeFieldToken[];
}

/** Which toolbar dropdown is open; only one at a time. */
type OpenMenu = 'lists' | 'merge' | null;

interface IToolbarButton {
	/** Icon child; the accessible name comes from `label`, so the icon is decorative. */
	icon: ReactNode;
	isActive: boolean;
	/** Accessible name (aria-label) — also the value the tests query buttons by. */
	label: string;
	onClick: () => void;
	/** Append a chevron to mark the button as a dropdown trigger. */
	hasChevron?: boolean;
}

function ToolbarButton({ icon, isActive, label, onClick, hasChevron }: IToolbarButton) {
	return (
		<button
			type='button'
			aria-label={label}
			aria-pressed={isActive}
			style={{ ...styles.toolbarButton, ...(isActive ? styles.toolbarButtonActive : undefined) }}
			onClick={onClick}
		>
			{icon}
			{hasChevron && <ChevronDownIcon />}
		</button>
	);
}

export const RichTextEditor = ({
	initialContent,
	onChange,
	onBlur,
	placeholder,
	showFormatting = true,
	showLists = true,
	showMergeField = true,
	renderMergeField,
	paragraphStyle = LEGACY_BLOCK_STYLE,
	surfaceStyle,
	mergeFieldTokens,
}: IProps) => {
	const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
	const listsMenuRef = useRef<HTMLDivElement>(null);
	const mergeMenuRef = useRef<HTMLDivElement>(null);

	// useEditor's onUpdate captures the closure from first render; route through a ref so a
	// consumer passing a fresh onChange each render (the common case) always gets the latest.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const onBlurRef = useRef(onBlur);
	onBlurRef.current = onBlur;

	useEffect(() => {
		if (!openMenu) {
			return;
		}
		const onMouseDown = (event: MouseEvent) => {
			const target = event.target as Node;
			// Close only when the click lands outside whichever menu is open (and its trigger).
			if (!listsMenuRef.current?.contains(target) && !mergeMenuRef.current?.contains(target)) {
				setOpenMenu(null);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setOpenMenu(null);
			}
		};
		document.addEventListener('mousedown', onMouseDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('mousedown', onMouseDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [openMenu]);

	const editor = useEditor({
		content: initialContent,
		editorProps: {
			attributes: {
				// Inline on the contenteditable itself (the embed cannot ship CSS files):
				// without min-height the editable is only as tall as its content, leaving
				// most of the surface a dead zone that doesn't focus on click.
				style: 'min-height: 144px; padding: 8px 12px; outline: none;',
			},
		},
		extensions: [
			StarterKit.configure({
				paragraph: { HTMLAttributes: { style: paragraphStyle } },
				bulletList: { HTMLAttributes: { style: BULLET_LIST_STYLE } },
				orderedList: { HTMLAttributes: { style: ORDERED_LIST_STYLE } },
				listItem: { HTMLAttributes: { style: LIST_ITEM_STYLE } },
			}),
			MergeField.configure({ tokens: mergeFieldTokens ? [...mergeFieldTokens] : [] }),
		],
		immediatelyRender: true,
		shouldRerenderOnTransaction: true,
		onUpdate: ({ editor: e }) => onChangeRef.current(e.getHTML(), { isEmpty: e.isEmpty }),
		onBlur: () => onBlurRef.current?.(),
	});

	if (!editor) {
		return null;
	}

	// A custom control always renders; the built-in dropdown only when showMergeField is on.
	const showMergeControl = !!renderMergeField || showMergeField;

	return (
		<div style={styles.container}>
			<div style={styles.toolbar} role='toolbar' aria-label='Formatting'>
				{showFormatting && (
					<>
						<ToolbarButton
							icon={<BoldIcon />}
							isActive={editor.isActive('bold')}
							label='Bold'
							onClick={() => editor.chain().focus().toggleBold().run()}
						/>
						<ToolbarButton
							icon={<ItalicIcon />}
							isActive={editor.isActive('italic')}
							label='Italic'
							onClick={() => editor.chain().focus().toggleItalic().run()}
						/>
						<ToolbarButton
							icon={<UnderlineIcon />}
							isActive={editor.isActive('underline')}
							label='Underline'
							onClick={() => editor.chain().focus().toggleUnderline().run()}
						/>
						{showLists && (
							<>
								<div style={styles.toolbarDivider} aria-hidden='true' />
								<div ref={listsMenuRef} style={styles.menuWrapper}>
									<ToolbarButton
										icon={<ListIcon />}
										hasChevron
										isActive={openMenu === 'lists' || editor.isActive('bulletList') || editor.isActive('orderedList')}
										label='Lists'
										onClick={() => setOpenMenu(menu => (menu === 'lists' ? null : 'lists'))}
									/>
									{openMenu === 'lists' && (
										<div style={styles.menu}>
											<button
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().toggleBulletList().run();
													setOpenMenu(null);
												}}
											>
												<ListIcon />
												Bullet list
											</button>
											<button
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().toggleOrderedList().run();
													setOpenMenu(null);
												}}
											>
												<ListOrderedIcon />
												Numbered list
											</button>
										</div>
									)}
								</div>
							</>
						)}
						{showMergeControl && <div style={styles.toolbarDivider} aria-hidden='true' />}
					</>
				)}
				{showMergeControl && (
					<div ref={mergeMenuRef} style={styles.menuWrapper}>
						{renderMergeField ? (
							renderMergeField(field => editor.chain().focus().insertMergeField(field).run())
						) : (
							<>
								<ToolbarButton
									icon={<BracesIcon />}
									hasChevron
									isActive={openMenu === 'merge'}
									label='Merge field'
									onClick={() => setOpenMenu(menu => (menu === 'merge' ? null : 'merge'))}
								/>
								{openMenu === 'merge' && (
									<div style={styles.menu}>
										{CONTACT_MERGE_FIELDS.map(field => (
											<button
												key={field.value}
												type='button'
												style={styles.menuItem}
												onClick={() => {
													editor.chain().focus().insertMergeField(field).run();
													setOpenMenu(null);
												}}
											>
												{field.text}
											</button>
										))}
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>
			<div style={{ ...styles.editorSurface, ...surfaceStyle }}>
				{!!placeholder && editor.isEmpty && <div style={styles.placeholder}>{placeholder}</div>}
				<EditorContent editor={editor} style={styles.editor} />
			</div>
		</div>
	);
};
